import React from 'react';
import {
  AgnesModal,
  AgnesInput,
  AgnesTextarea,
  AgnesButton,
  AgnesLabel,
  AgnesSelect,
  AgnesSelectTrigger,
  AgnesSelectValue,
  AgnesSelectContent,
  AgnesSelectItem,
} from '@agnes/ui';
import { notify } from '@/stores/useToast';
import type { StoreApi, UseBoundStore } from 'zustand';
import type { IncidenciaSpec } from '@/lib/incidenciaSpecs';
import type { LinhaIncidencia, FormIncidencia } from '@/stores/incidenciaStoreFactory';

type Store = UseBoundStore<StoreApi<any>>;

function vazio(spec: IncidenciaSpec): FormIncidencia {
  const f: Record<string, unknown> = { code: '', name: '', notes: null };
  spec.campos.forEach((c) => {
    if (!(c.key in f)) f[c.key] = c.type === 'date' || c.boolean ? null : '';
  });
  return f as FormIncidencia;
}

export default function ModalIncidencia({
  open,
  onClose,
  linha,
  spec,
  useStore,
}: {
  open: boolean;
  onClose: () => void;
  linha: LinhaIncidencia | null;
  spec: IncidenciaSpec;
  useStore: Store;
}) {
  const salvar = useStore((s: any) => s.salvar);
  const salvando = useStore((s: any) => s.salvando);
  const editando = useStore((s: any) => s.editando);
  const [form, setForm] = React.useState<FormIncidencia>(vazio(spec));

  React.useEffect(() => {
    if (open) {
      if (linha) {
        const f: Record<string, unknown> = {
          code: linha.code,
          name: linha.name,
          notes: linha.notes,
        };
        spec.campos.forEach((c) => {
          if (!(c.key in f)) {
            const raw = (linha as any)[c.key];
            f[c.key] = raw ?? (c.type === 'date' || c.boolean ? null : '');
          }
        });
        setForm(f as FormIncidencia);
      } else {
        setForm(vazio(spec));
      }
    }
  }, [open, linha, spec]);

  const set = (k: string) => (v: string) => setForm((s) => ({ ...s, [k]: v }));

  const submit = async () => {
    if (!String(form.code).trim() || !String(form.name).trim()) {
      notify.error('Código e nome são obrigatórios.');
      return;
    }
    const faltando = spec.campos.find((c) => {
      if (!c.required) return false;
      const v = (form as any)[c.key];
      return v === null || v === undefined || v === '';
    });
    if (faltando) {
      notify.error(`${faltando.label} é obrigatório.`);
      return;
    }
    try {
      await salvar(form);
      onClose();
    } catch {
      /* notificado no store */
    }
  };

  return (
    <AgnesModal
      open={open}
      title={editando ? `Editar ${spec.titulo}` : `Nova - ${spec.titulo}`}
      maxWidth="md"
      onClose={onClose}
      footer={
        <div className="flex justify-end gap-2">
          <AgnesButton variant="text" onClick={onClose}>
            Cancelar
          </AgnesButton>
          <AgnesButton variant="primary" disabled={salvando} onClick={submit}>
            Salvar
          </AgnesButton>
        </div>
      }
    >
      <div className="flex flex-col gap-4">
        {spec.campos.map((c) => {
          const val = (form as any)[c.key] ?? '';
          if (c.key === 'code') {
            return (
              <AgnesInput
                key={c.key}
                label={c.label}
                value={String(val)}
                disabled={!!editando}
                onChange={(e) => set('code')(e.target.value)}
              />
            );
          }
          if (c.type === 'select') {
            const current = c.boolean
              ? val === true
                ? 'true'
                : val === false
                  ? 'false'
                  : ''
              : val === '' || val == null
                ? ''
                : String(val);
            const onSel = (v: string) =>
              setForm((s) => ({ ...s, [c.key]: c.boolean ? v === 'true' : v }));
            return (
              <div key={c.key} className="flex flex-col gap-1">
                <AgnesLabel>
                  {c.label}
                  {c.required ? ' *' : ''}
                </AgnesLabel>
                <AgnesSelect value={current} onValueChange={onSel}>
                  <AgnesSelectTrigger className="w-full">
                    <AgnesSelectValue placeholder={`Selecione ${c.label.toLowerCase()}`} />
                  </AgnesSelectTrigger>
                  <AgnesSelectContent>
                    {c.options?.map((o) => (
                      <AgnesSelectItem key={o.value} value={o.value}>
                        {o.label}
                      </AgnesSelectItem>
                    ))}
                  </AgnesSelectContent>
                </AgnesSelect>
              </div>
            );
          }
          if (c.type === 'textarea') {
            return (
              <AgnesTextarea
                key={c.key}
                label={c.label}
                value={String(val)}
                onChange={(e) => set(c.key)(e.target.value)}
              />
            );
          }
          return (
            <AgnesInput
              key={c.key}
              label={c.label}
              type={c.type === 'date' ? 'date' : 'text'}
              value={String(val)}
              onChange={(e) => set(c.key)(e.target.value)}
            />
          );
        })}
      </div>
    </AgnesModal>
  );
}
